import { DataCache, IDataCommand, ScriniumServices, injectDataCache } from '@aesop-fables/scrinium';
import type { ICommandExecutor, IObservableQueryDispatcher } from '@aesop-fables/scrinium';
import { LearningCompartments, learningStorageKey } from '..';
import { inject } from '@aesop-fables/containr';
import { ApiKeys } from '../../../api/apis/ApiKeys';
import { CourseData, PublicationStatusEnum, UnitData } from '../../../models/CourseData';
import { UnitApi } from '../../../api/apis/UnitApi';
import { findUnitSource } from './utils';
import { PublishUnit } from './PublishUnit';
import { FindUnitById } from '../queries/FindUnitById';

function getChangedFields(original: UnitData, updated: UnitData): Partial<UnitData> {
  const changedFields: Partial<UnitData> = {};

  if (typeof updated.unitId !== 'undefined') {
    changedFields.unitId = updated.unitId;
  }

  for (const key in updated) {
    if (
      updated.hasOwnProperty(key) &&
      original[key as keyof UnitData] !== updated[key as keyof UnitData]
    ) {
      changedFields[key as keyof UnitData] = updated[key as keyof UnitData] as any;
    }
  }

  return changedFields;
}

export class AddOrUpdateUnit implements IDataCommand<UnitData, UnitData> {
  constructor(
    @injectDataCache(learningStorageKey) private readonly cache: DataCache<LearningCompartments>,
    @inject(ApiKeys.Unit) private readonly api: UnitApi,
    @inject(ScriniumServices.CommandExecutor) private readonly commands: ICommandExecutor,
    @inject(ScriniumServices.QueryDispatcher) private readonly queries: IObservableQueryDispatcher,
  ) {}

  async execute(unit: UnitData): Promise<UnitData> {
    let response: UnitData | undefined;

    await this.cache.modify<CourseData[]>('courses', async courses => {
      if (unit.sourceCourseId) {
        if (unit.unitId) {
          const currentUnit = await this.queries.execute(FindUnitById, unit.originalUnitId);
          const payload = getChangedFields(currentUnit ?? {}, unit);

          let { data: updatedUnit } =
            payload.icon === null || payload.background === null
              ? await this.api.replaceUnit(unit)
              : await this.api.updateUnit(payload);

          // only publish if original unit was published
          if (unit.status === PublicationStatusEnum.PUBLISHED) {
            const publishedUnit = await this.commands.execute(PublishUnit, updatedUnit);
            updatedUnit = publishedUnit;
          }

          const lessons = unit.lessons;
          updatedUnit.lessons = lessons;
          response = updatedUnit;

          const courseIndex = findUnitSource(unit, courses);
          const course = courses[courseIndex];

          const units = course.units ?? [];
          // editing a unit deactivates/deletes the original unit and creates a new entry for the updated unit
          const newUnits = units
            .map(u => {
              if (
                (unit.active || unit.status === PublicationStatusEnum.DRAFT) &&
                u.originalUnitId === unit.originalUnitId
              ) {
                // if the unit being edited is active, set active to false
                if (u.active) {
                  return { ...u, active: false };
                }
                // if the unit being edited is a draft, delete that unit
                return null;
              }
              return u;
            })
            .filter((unit): unit is UnitData => unit !== null);
          const updatedUnits = [...newUnits, updatedUnit];
          course.units = updatedUnits;

          const newCourses = [...courses];
          newCourses[courseIndex].units = updatedUnits;

          return newCourses;
        }

        const courseIndex = findUnitSource(unit, courses);
        const course = courses[courseIndex];
        const { data } = await this.api.createUnit(unit);
        const newUnits = [...(course.units ?? []), data];
        course.units = newUnits;
        response = data;

        const newCourses = [...courses];
        newCourses[courseIndex].units = newUnits;

        return newCourses;
      }
      return courses;
    });

    return response as UnitData;
  }
}
